import React from 'react'
import styled from 'styled-components'
import {
  WishListItemDTO,
  Link,
  usePrice,
  useWishlistItem,
  useConfigState,
} from '@sentecacommerce-theme/lib'
import {
  Price,
  LazyResponsiveImage,
  WishlistButton,
  BasketButton,
} from '../../../Common'

export const ProductCard = ({ item }: { item: WishListItemDTO }) => {
  const {
    unitPrice: priceRaw,
    unitDiscountedPrice: discountedPriceRaw,
    images,
    name,
    productId,
    variantId,
    offerId,
    slug,
    getCustomFieldByKey,
    inventory,
  } = useWishlistItem({ item })

  const config = useConfigState()

  const hideRegularPrice = getCustomFieldByKey?.(
    config?.customFieldKeys?.hideRegularPrice
  )

  const getPrice = () => {
    if (!hideRegularPrice) return priceRaw

    if (discountedPriceRaw?.centAmount) return discountedPriceRaw

    return priceRaw
  }

  const { price, discountPrice } = usePrice({
    price: getPrice(),
    discountedPrice: !hideRegularPrice ? discountedPriceRaw : undefined,
  })

  const isOnlyInPhysicalStores = getCustomFieldByKey?.(
    config?.customFieldKeys?.isOnlyInPhysicalStores
  )

  return (
    <Link passHref href={`/${slug}/p`}>
      <Container>
        <ImageContainer>
          <LazyResponsiveImage
            breakpointSizes={{
              xs: '50vw',
              sm: '50vw',
              md: '30vw',
              lg: '180px',
              xl: '180px',
              xxl: '180px',
            }}
            defaultImageWidth="40vw"
            imagesMap={images?.[0]}
            alt={name}
          />
        </ImageContainer>
        <ProductInfoContainer>
          <ProductTitle>{name}</ProductTitle>
        </ProductInfoContainer>
        <CTAWrapper>
          <PriceWrapper>
            <Price
              price={price.value}
              size="small"
              hideDiscountPercentage
              discountedPrice={discountPrice ? discountPrice.value : undefined}
              currency={price.currency}
              hideRegularPrice={hideRegularPrice}
            />
          </PriceWrapper>
          <CartWrapper>
            {!isOnlyInPhysicalStores && (
              <BasketButton
                productId={productId}
                variantId={variantId}
                type="icon"
                maxQuantity={1}
                offerId={offerId as string}
                canOrder={inventory.hasAvailableQuantity}
              />
            )}
          </CartWrapper>
        </CTAWrapper>
        <FavIconWrapper>
          <WishlistButton variantId={variantId} productId={productId} />
        </FavIconWrapper>
      </Container>
    </Link>
  )
}

const Container = styled.a`
  text-decoration: none;
  display: block;
  width: 100%;
  border-radius: 1rem;
  border: 0.1rem solid ${({ theme }) => theme.palette.silverTree};
  overflow: hidden;
  position: relative;
  background-color: ${({ theme }) => theme.palette.secondary};
  transition: all 0.2s ease-in-out;

  &:hover {
    box-shadow: 0px 1rem 1.5rem 0px rgba(0, 0, 0, 0.15);
    margin-top: -0.2rem;
  }
`

const ImageContainer = styled.div`
  overflow: hidden;
  position: relative;
  text-align: center;
  padding: 2rem;
  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    height: auto;
  }
  cursor: pointer;
`

const ProductInfoContainer = styled.div.attrs({
  className: 'mb-4 py-0 px-4',
})`
  position: relative;
  height: 6rem;
  @media only screen and (max-width: 480px) {
    margin-bottom: 1rem;
  }

  @media only screen and (${({ theme }) => theme.breakpoints.xs.max}) {
    margin-bottom: 1rem;
  }

  cursor: pointer;
`

const ProductTitle = styled.span`
  font-size: 1.6rem;
  color: ${({ theme }) => theme.palette.greyTitle};
  font-weight: 500;
  line-height: 1.2rem;
  margin-bottom: 0;
  white-space: normal;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  line-height: 2rem;
  height: 100%;
  min-height: 6rem;
  letter-spacing: normal;

  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    font-size: 1.4rem;
    margin-top: 1rem;
  }
`

const CartWrapper = styled.div`
  & div {
    position: relative;
    top: -0.2rem;
  }
  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    margin-right: 0.5rem;
  }
  cursor: pointer;
  &:hover {
    transform: scale(1.3);
  }
  transition: all 0.3s;
`

const CTAWrapper = styled.div.attrs({
  className: 'd-flex align-items-center pt-0 px-4 pb-4',
})`
  position: relative;
  align-items: center;
  display: flex !important;
  height: 5rem;

  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    padding: 0 0 1.5rem 1.5rem;
  }
`

const PriceWrapper = styled.div`
  color: ${({ theme }) => theme.palette.primary};
  font-size: 1.8rem;
  margin-right: auto;

  @media only screen and (${({ theme }) => theme.breakpoints.sm.max}) {
    font-size: 1.4rem;
  }
`
const FavIconWrapper = styled.div`
  color: ${({ theme }) => theme.palette.silverTree};
  cursor: pointer;
  position: absolute;
  top: 0;
  right: 0;

  & svg {
    width: 3.2rem;
    height: 3.2rem;
    transition: background-position 1s steps(28);
    transition-duration: 0s;
    position: absolute;
    right: 1rem;
    top: 0.5rem;
    z-index: 100;
    transition: all 0.3s;

    &:hover {
      transform: scale(1.3);
    }
  }
`
